import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { communityService, Disease } from '../../services/communityService';
import PageHeader from '../../components/PageHeader';

export default function CreatePostPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedDiseaseCode = location.state?.diseaseCode;

  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [formData, setFormData] = useState({
    diseaseCode: preselectedDiseaseCode || '',
    type: 'GENERAL',
    title: '',
    content: '',
    isAnonymous: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDiseases();
  }, []);

  const loadDiseases = async () => {
    try {
      const data = await communityService.getDiseases();
      setDiseases(data);
    } catch (err) {
      console.error('Error loading diseases:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.content.trim()) {
      setError('Conținutul este obligatoriu');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const post = await communityService.createPost({
        diseaseCode: formData.diseaseCode || undefined,
        type: formData.type,
        title: formData.title || undefined,
        content: formData.content,
        isAnonymous: formData.isAnonymous
      });

      // Navigate to the created post
      navigate(`/community/posts/${post.id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la crearea postării');
    } finally {
      setLoading(false);
    }
  };

  const postTypes = [
    { value: 'GENERAL', label: '📝 General', description: 'Postare generală' },
    { value: 'QUESTION', label: '❓ Întrebare', description: 'Ai o întrebare?' },
    { value: 'EXPERIENCE', label: '📖 Experiență', description: 'Împărtășește experiența ta' },
    { value: 'VICTORY', label: '🎉 Victorie', description: 'Celebrează o realizare' },
    { value: 'TIP', label: '💡 Sfat', description: 'Oferă un sfat util' },
    { value: 'SUPPORT', label: '💚 Suport', description: 'Cere sau oferă suport' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Post nou" />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Înapoi</span>
        </button>

        {/* Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Creează o postare nouă
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Disease selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comunitate (opțional)
              </label>
              <select
                value={formData.diseaseCode}
                onChange={(e) => setFormData({ ...formData, diseaseCode: e.target.value })}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Generală</option>
                {diseases.map(disease => (
                  <option key={disease.code} value={disease.code}>
                    {disease.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Alege o comunitate pentru ca postarea ta să fie mai relevantă
              </p>
            </div>

            {/* Post type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipul postării
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {postTypes.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.value })}
                    className={`text-left p-4 rounded-lg border-2 transition-all ${
                      formData.type === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900 mb-1">
                      {type.label}
                    </div>
                    <div className="text-sm text-gray-500">
                      {type.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titlu (opțional)
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Adaugă un titlu pentru postarea ta..."
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={200}
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conținut <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Scrie aici conținutul postării tale..."
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={10}
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                {formData.content.length} caractere
              </p>
            </div>

            {/* Anonymous option */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="anonymous"
                checked={formData.isAnonymous}
                onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="anonymous" className="text-sm text-gray-700">
                Postează anonim (numele tău nu va fi afișat)
              </label>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={loading || !formData.content.trim()}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                {loading ? 'Se publică...' : 'Publică'}
              </button>
            </div>
          </form>
        </div>

        {/* Guidelines */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">
            📋 Ghid pentru postări
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Fii respectuos și empatic cu ceilalți membri</li>
            <li>• Nu împărtăși informații medicale sensibile</li>
            <li>• Evită limbajul ofensator sau discursul de ură</li>
            <li>• Verifică informațiile înainte de a le împărtăși</li>
            <li>• Respectă confidențialitatea altora</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
